const STORAGE_KEY = 'tms.locale';
// Records a language choice the server never accepted. Without it a failed save
// silently reverts on the next load, because the profile wins over this device.
const UNSAVED_KEY = `${STORAGE_KEY}:unsaved`;
const DEFAULT_LOCALE = 'en';
const TIME_ZONE = 'America/Los_Angeles';

const translations = {
  en: {
    'meta.englishFallback': 'English fallback',
    'language.label': 'Language',
    'language.english': 'EN',
    'language.chinese': '中文',
    // Deliberately does not promise the choice will be lost on reload: the
    // unsaved marker keeps it on this device until the save succeeds.
    'language.notSaved': "Language changed on this device, but couldn't be saved to your account.",
    'common.loadingDashboard': 'Loading TMS dashboard...',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.total': 'Total',
    'common.back': 'Back',
    'common.retry': 'Retry',
    'loading.containers': 'Loading Containers',
    'loading.containerDetail': 'Loading Container details',
    'loading.failed': 'The requested data could not be loaded.',
    'weather.currentAria': 'Current Long Beach weather',
    'weather.loading': 'Loading weather',
    'weather.unavailable': 'Weather unavailable',
    'weather.stale': 'Saved · {{condition}}',
    'weather.label': 'Weather',
    'weather.clear': 'Clear',
    'weather.partlyCloudy': 'Partly cloudy',
    'weather.overcast': 'Overcast',
    'weather.fog': 'Fog',
    'weather.rain': 'Rain',
    'weather.snow': 'Snow',
    'weather.thunderstorms': 'Thunderstorms',
    'login.panelLabel': 'KM login',
    'login.product': 'Transportation Management System',
    'login.accountEmail': 'Account Email',
    'login.password': 'Password',
    'login.submit': 'Login',
    'login.welcomeBack': 'Welcome back',
    'login.continue': 'Continue',
    'login.showPassword': 'Show password',
    'login.hidePassword': 'Hide password',
    'login.invalidCredentials': 'Invalid email or password.',
    'login.accountDisabled': 'This account is disabled. Contact an administrator.',
    'login.tooManyAttempts': 'Too many attempts. Wait a moment, then try again.',
    'login.networkError': 'Unable to reach Firebase. Check your connection and try again.',
    'login.serviceError': 'Sign-in is temporarily unavailable. Please try again.',
    'login.noRole': 'Your account has no assigned workspace. Contact an administrator.',
    'login.sessionError': 'Signed in, but account permissions could not be loaded. Please try again.',
    'nav.dashboard': 'Dashboard',
    'nav.containers': 'Containers',
    'nav.addContainer': 'Add Container',
    'nav.shipment': 'Shipment',
    'nav.customers': 'Customers',
    'nav.drivers': 'Drivers',
    'nav.status': 'Status',
    'nav.companies': 'Companies',
    'nav.team': 'Team',
    'nav.chassis': 'Chassis',
    'nav.customerQuotes': 'Quotes',
    'nav.settings': 'Settings',
    'nav.add': 'Add',
    'nav.addCustomer': 'Add Customer',
    'nav.addDriver': 'Add Driver',
    'nav.customerPortal': 'Customer Portal',
    'nav.addTask': 'Add Task',
    'nav.bulkAdd': 'Bulk Add',
    'nav.actions': 'Action Required',
    'customerDashboard.title': 'My Containers',
    'customerDashboard.subtitle': 'Priority-sorted public milestones for containers linked to your account.',
    'customerDashboard.loading': 'Loading your container dashboard...',
    'customerDashboard.empty': 'No containers are currently available.',
    'customerDashboard.error': 'Your container dashboard could not be loaded. No private operational data was shown.',
    'customerDashboard.retry': 'Retry',
    'customerDashboard.refresh': 'Refresh',
    'customerDashboard.refreshWarning': 'Refresh failed. The previously loaded safe information remains visible.',
    'customerDashboard.consistency': 'Updates may take a short moment to appear after operations records a milestone.',
    'customerDashboard.completionRule': 'Loaded Delivered is an intermediate milestone. Only Empty Returned completes the Container.',
    'customerDashboard.moreUnavailable': 'More records may exist, but safe pagination is temporarily unavailable.',
    'customerDashboard.pagination': 'Load more container records',
    'customerDashboard.more.operational': 'Load more active containers',
    'customerDashboard.more.due': 'Load more due containers',
    'customerDashboard.more.completed': 'Load more completed containers',
    'customerDashboard.notProvided': 'Not provided',
    'customerDashboard.count': '{{count}} containers',
    'customerDashboard.groupEmpty': 'No containers in this priority group.',
    'customerDashboard.group.attention': 'Attention',
    'customerDashboard.group.in_progress': 'In Progress',
    'customerDashboard.group.upcoming': 'Upcoming',
    'customerDashboard.group.completed_recent': 'Recently Completed',
    'customerDashboard.viewDetail': 'View public milestones',
    'customerDashboard.detail.title': 'Container details',
    'customerDashboard.detail.eyebrow': 'Public container milestones',
    'customerDashboard.detail.loading': 'Loading container details...',
    'customerDashboard.detail.error': 'This container is unavailable or is not linked to your account.',
    'customerDashboard.field.dcContainer': 'DC Container',
    'customerDashboard.field.job': 'Job',
    'customerDashboard.field.terminal': 'Terminal',
    'customerDashboard.field.ssl': 'SSL',
    'customerDashboard.field.sizeType': 'Size / Type',
    'customerDashboard.field.status': 'Status',
    'customerDashboard.field.stage': 'Current stage',
    'customerDashboard.field.eta': 'ETA',
    'customerDashboard.field.lfd': 'LFD',
    'customerDashboard.field.due': 'Due',
    'customerDashboard.field.pickupAppointment': 'Pickup appointment',
    'customerDashboard.field.deliveryAppointment': 'Delivery appointment',
    'customerDashboard.field.loadOut': 'Load out',
    'customerDashboard.field.loadedDelivered': 'Loaded delivered',
    'customerDashboard.field.emptyReady': 'Empty ready',
    'customerDashboard.field.emptyPickedUp': 'Empty picked up',
    'customerDashboard.field.emptyReturned': 'Empty returned',
    'customerDashboard.status.pending': 'Pending',
    'customerDashboard.status.dispatched': 'Dispatched',
    'customerDashboard.status.in_progress': 'In progress',
    'customerDashboard.status.loaded_delivered': 'Loaded delivered',
    'customerDashboard.status.empty_ready': 'Empty ready',
    'customerDashboard.status.returning_empty': 'Returning empty',
    'customerDashboard.status.empty_returned': 'Empty returned',
    'customerDashboard.status.picked_up_from_terminal': 'Picked up from terminal',
    'customerDashboard.status.arrived_at_warehouse': 'Arrived at warehouse',
    'customerDashboard.status.out_for_delivery': 'Out for delivery',
    'nav.tasks': 'Tasks',
    'nav.users': 'Users',
    'shell.noRole': 'no role',
    'shell.logout': 'Logout',
    'shell.profile': 'Profile',
    'shell.openNavigation': 'Open navigation',
    'shell.closeNavigation': 'Close navigation',
    'shell.primaryNavigation': 'Primary navigation',
    'shell.noRoleAssigned': 'No role assigned. Contact admin.',
    'dispatch.edit.open': 'Edit current task',
    'dispatch.edit.title': 'Edit Dispatch Task',
    'dispatch.edit.notice': 'Schedule, route, and notes changes are saved to the Audit Log.',
    'dispatch.edit.scheduledStart': 'Scheduled start *',
    'dispatch.edit.scheduledEnd': 'Scheduled end',
    'dispatch.edit.originName': 'Origin name',
    'dispatch.edit.originAddress': 'Origin address',
    'dispatch.edit.originType': 'Origin type',
    'dispatch.edit.destinationName': 'Destination name',
    'dispatch.edit.destinationAddress': 'Destination address',
    'dispatch.edit.destinationType': 'Destination type',
    'dispatch.edit.notes': 'Dispatcher notes',
    'dispatch.edit.save': 'Save task changes',
    'dispatch.edit.saving': 'Saving changes…',
    'dispatch.edit.success': 'Dispatch Task updated.',
    'dispatch.point.terminal': 'Terminal',
    'dispatch.point.yard': 'Yard',
    'dispatch.point.customer': 'Customer',
    'dispatch.edit.error.EDIT_PERMISSION_DENIED': 'You do not have permission to edit this Dispatch Task.',
    'dispatch.edit.error.EDIT_TASK_NOT_FOUND': 'This Dispatch Task is no longer available.',
    'dispatch.edit.error.EDIT_REQUEST_INVALID': 'Review the schedule, route, and notes before saving.',
    'dispatch.edit.error.EDIT_STATE_CHANGED': 'The task changed while you were editing. Refresh and review it again.',
    'dispatch.edit.error.EDIT_TERMINAL_TASK': 'A completed, cancelled, or failed task cannot be edited.',
    'dispatch.edit.error.EDIT_ROUTE_STATE_INVALID': 'Schedule and route can only change before the Driver starts the route.',
    'dispatch.edit.error.EDIT_REQUEST_CONFLICT': 'This edit request conflicts with an earlier request. Refresh before retrying.',
    'dispatch.edit.error.EDIT_WRITE_FAILED': 'The edit was not confirmed. Retry the same request or refresh the task.',
    'dispatch.progress.accept': 'Accept task',
    'dispatch.progress.start': 'Start route',
    'dispatch.progress.arrived': 'Arrived',
    'dispatch.progress.complete': 'Complete transport task',
    'dispatch.progress.failed': 'Mark task failed',
    'dispatch.progress.updated': 'Task progress updated.',
    'dispatch.progress.notice': 'These controls update your Dispatch Task only. Transport task completion does not complete the Container; only Empty Returned completes it.',
    'error.somethingWrong': 'Something went wrong',
    'dispatch.title': 'Official Dispatch',
    'dispatch.authorityNotice': 'This creates a formal Dispatch Task. Container Driver text is not proof of assignment.',
    'dispatch.driver': 'Eligible Driver',
    'dispatch.selectDriver': 'Select an eligible Driver',
    'dispatch.legType': 'Transport leg',
    'dispatch.scheduledStart': 'Scheduled start *',
    'dispatch.confirm': 'Confirm and create task',
    'dispatch.creating': 'Creating task...',
    'dispatch.success': 'Formal Dispatch Task created.',
    'dispatch.notOfficiallyDispatched': 'Not officially dispatched',
    'dispatch.dataReview': 'Dispatch data needs review',
    'dispatch.taskCancelledNotDispatched': 'Task cancelled — not officially dispatched',
    'dispatch.taskFailedNotDispatched': 'Task failed — not officially dispatched',
    'dispatch.taskCompletedNoActiveDriver': 'Transport task completed — no active Driver',
    'dispatch.noEligibleDrivers': 'No server-verified eligible Driver accounts are available.',
    'dispatch.driverListError': 'Eligible Drivers could not be loaded. Refresh before trying again.',
    'dispatch.searchDrivers': 'Search eligible Drivers by name',
    'dispatch.driverListTruncated': 'More matching Drivers exist. Narrow the name search to find the correct account.',
    'dispatch.nextDrivers': 'Next Driver results',
    'dispatch.statusLoadError': 'Dispatch status could not be loaded. Refresh before trying again.',
    'dispatch.anomalyTitle': 'Dispatch data anomaly',
    'dispatch.activeConflictTitle': 'Active Dispatch Task exists',
    'dispatch.viewCurrentTask': 'View current task',
    'dispatch.cancel.historyWarning': 'The current task will be cancelled, not deleted. Its history and Audit Log remain available. A replacement task is created separately.',
    'dispatch.cancel.reason': 'Cancellation reason *',
    'dispatch.cancel.selectReason': 'Select a cancellation reason',
    'dispatch.cancel.explanation': 'Other reason explanation *',
    'dispatch.cancel.confirm': 'Confirm task cancellation',
    'dispatch.cancel.cancelling': 'Cancelling task…',
    'dispatch.cancel.retry': 'Retry cancellation',
    'dispatch.cancel.retrying': 'Checking cancellation result…',
    'dispatch.cancel.success': 'Previous task cancelled',
    'dispatch.cancel.separateAssignment': 'No replacement task was created. Continue only when you are ready to make a separate formal assignment.',
    'dispatch.cancel.continue': 'Continue to new assignment',
    'dispatch.cancel.notAuthorized': 'Only an authorized Admin or Dispatcher can cancel this task.',
    'dispatch.cancel.error.CANCELLATION_PERMISSION_DENIED': 'You do not have permission to cancel this Dispatch Task.',
    'dispatch.cancel.error.CANCELLATION_TASK_NOT_FOUND': 'This Dispatch Task is no longer available. Refresh before continuing.',
    'dispatch.cancel.error.CANCELLATION_REASON_INVALID': 'Select a cancellation reason. Other reason requires an explanation of 1 to 500 characters.',
    'dispatch.cancel.error.CANCELLATION_NOT_ACTIVE': 'This task is no longer active and cannot be cancelled.',
    'dispatch.cancel.error.CANCELLATION_DATA_ANOMALY': 'Dispatch data needs administrator review. The task was not cancelled.',
    'dispatch.cancel.error.CANCELLATION_STATE_CHANGED': 'The task changed while you were working. Refresh and review the current task.',
    'dispatch.cancel.error.CANCELLATION_REQUEST_CONFLICT': 'This cancellation request conflicts with an earlier request. Refresh before trying again.',
    'dispatch.cancel.error.CANCELLATION_WRITE_FAILED': 'Cancellation was not confirmed. Refresh the task status before trying again.',
    'dispatch.error.ASSIGNMENT_ACTIVE_CONFLICT': 'An Active Dispatch Task already exists. View or cancel the current task before creating another.',
    'dispatch.error.ASSIGNMENT_DATA_ANOMALY': 'Dispatch data needs administrator review. No new task can be created.',
    'dispatch.error.ASSIGNMENT_DRIVER_NOT_ELIGIBLE': 'This Driver is not eligible for assignment. Select another eligible Driver.',
    'dispatch.error.ASSIGNMENT_DRIVER_ACCOUNT_NOT_LINKED': 'This Driver has no linked login account and cannot receive tasks. Link a Driver account first, or select another Driver.',
    'dispatch.error.ASSIGNMENT_SCHEDULE_REQUIRED': 'Select a scheduled start date and time.',
    'dispatch.error.ASSIGNMENT_SCHEDULE_IN_PAST': 'Scheduled start cannot be more than five minutes before server time.',
    'dispatch.error.ASSIGNMENT_PERMISSION_DENIED': 'You do not have permission to perform this dispatch action.',
    'dispatch.error.ASSIGNMENT_CONTAINER_NOT_FOUND': 'This Container is no longer available. Return to the Container list and refresh.',
    'dispatch.error.ASSIGNMENT_NEXT_LEG_INVALID': 'The selected transport leg is no longer valid. Refresh the Container before continuing.',
    'dispatch.error.ASSIGNMENT_STATE_CHANGED': 'Dispatch status changed while you were working. Review the latest status before continuing.',
    'dispatch.error.ASSIGNMENT_WRITE_FAILED': 'The task could not be created. No success has been confirmed. Refresh the dispatch status before trying again.',
    'test.welcome': 'Welcome, {{name}}!',
    'dashboard.title': 'Worker Dashboard',
    'dashboard.inactive': 'Inactive',
    'dashboard.active': 'Active',
    'dashboard.urgent': 'Urgent',
    'dashboard.dueToday': 'Due Today',
    'dashboard.kpi.total': 'Total Containers',
    'dashboard.kpi.active': 'Active Containers',
    'dashboard.kpi.risk': 'At Risk',
    'dashboard.kpi.completed7d': 'Completed (7d)',
    'dashboard.kpi.revenue7d': 'Revenue (7d)',
    'dashboard.kpi.dueToday': 'Due Today',
    'dashboard.kpi.vsLastMonth': 'vs last month',
    'dashboard.kpi.trend': '{{label}} monthly trend',
    'dashboard.emptyReturned': 'Empty Returned',
    'dashboard.monthStatus': 'This Month Status',
    'dashboard.completionHealth': 'Completion Health',
    'dashboard.stageProgress': 'Stage Progress',
    'dashboard.emptyReturnedOnTime': 'Empty Returned On Time',
    'dashboard.stagePickedUp': 'Picked Up',
    'dashboard.stageWarehouseArrived': 'Warehouse Arrived',
    'dashboard.stageReturned': 'Empty Returned',
    'dashboard.tableTitle': 'Dashboard Containers',
    'dashboard.noContainers': 'No containers found.',
    'dashboard.container': 'Container',
    'dashboard.dcContainer': 'DC Container',
    'dashboard.job': 'Job',
    'dashboard.customer': 'Customer',
    'dashboard.customerLink': 'Customer Link',
    'dashboard.terminal': 'Terminal',
    'dashboard.ssl': 'SSL',
    'dashboard.eta': 'ETA',
    'dashboard.lfd': 'LFD',
    'dashboard.due': 'Due',
    'dashboard.pickupAppointment': 'Pickup Appt',
    'dashboard.deliveryAppointment': 'Delivery Appt',
    'dashboard.status': 'Status',
    'dashboard.driver': 'Driver',
    'dashboard.lastNote': 'Last Note',
    'dashboard.charges': 'Charges',
    'dashboard.quickStatus': 'Quick Status',
    'dashboard.edit': 'Edit',
    'dashboard.unassigned': 'Unassigned',
    'dashboard.yes': 'Yes',
    'dashboard.locked': 'Locked',
    'dashboard.editContainer': 'Edit container',
    'admin.users.title': 'Admin Users',
    'admin.users.roleNotice': 'Role changes update Firebase custom claims. The changed user must sign out and sign back in.',
    'admin.users.name': 'Name',
    'admin.users.email': 'Email',
    'admin.users.role': 'Role',
    'admin.users.customerUid': 'Customer UID',
    'admin.users.customerRef': 'Customer Ref',
    'admin.users.active': 'Active',
    'admin.users.yes': 'Yes',
    'admin.users.no': 'No',
    'admin.users.roleUpdated': 'Role updated. User must sign out and sign back in.',
    'admin.users.roleUpdateFailed': 'Role update failed.',
    'audit.title': 'Admin Log',
    'audit.subtitle': 'Immutable business activity history. Loads 100 records per page.',
    'audit.pageSize': '100 records per page',
    'audit.startDate': 'Start date',
    'audit.startTime': 'Start time',
    'audit.endDate': 'End date',
    'audit.endTime': 'End time',
    'audit.search': 'Search audit log',
    'audit.clear': 'Clear',
    'audit.time': 'Time',
    'audit.actor': 'Actor',
    'audit.item': 'Changed Item',
    'audit.summary': 'Summary',
    'audit.details': 'Details',
    'audit.older': 'Older',
    'audit.newer': 'Newer',
    'audit.pagination': 'Audit log pagination',
    'audit.loading': 'Loading audit history...',
    'audit.empty': 'No business audit events were found for this range.',
    'audit.collection': 'Collection',
    'audit.documentId': 'Document ID',
    'audit.operation': 'Operation',
    'audit.role': 'Actor role',
    'audit.eventTime': 'Event timestamp',
    'audit.changedFields': 'Changed fields',
    'audit.previous': 'Previous value',
    'audit.next': 'New value',
    'audit.reason': 'Reason',
    'audit.logId': 'Audit log ID',
    'audit.technical': 'Technical details',
    'audit.showDetails': 'Show details for {{item}}',
    'audit.hideDetails': 'Hide details for {{item}}',
    'audit.loadError': 'Audit history could not be loaded.',
    'audit.invalidRange': 'End date and time must be later than start date and time.',
    'audit.missingRange': 'Select a complete start and end date and time.',
    'audit.longRange': 'This range is longer than 90 days and may require several pages. Continue?',
    'audit.newEvents': '{{count}} new audit events',
    'audit.newEvent': '1 new audit event',
    'team.title': 'Team',
    'team.operationalSummary': 'Team operational summary',
    'team.operationsDirectory': 'Operations directory',
    'team.totalEmployees': 'Total Employees',
    'team.activeToday': 'Active Today',
    'team.onDuty': 'On Duty',
    'team.onLeave': 'On Leave',
    'team.vsLast7Days': 'vs last 7 days',
    'team.sevenDayTrend': '7-day trend',
    'team.trendUnavailable': '—',
    'team.members': 'Team Members',
    'team.memberCount': '{{count}} members',
    'team.showingAllMembers': 'Showing all {{count}} members',
    'team.employeeName': 'Employee Name',
    'team.employeeId': 'Employee ID',
    'team.role': 'Role',
    'team.department': 'Department',
    'team.status': 'Status',
    'team.lastActive': 'Last Active',
    'team.actions': 'Actions',
    'team.actionsFor': 'Actions for {{name}}',
    'team.noMembers': 'No team members are available.',
    'team.unnamedMember': 'Unnamed member',
    'team.status.active': 'Active',
    'team.status.offline': 'Offline',
    'team.status.onLeave': 'On Leave',
    'team.status.inactive': 'Inactive',
    'team.role.admin': 'Admin',
    'team.role.dispatcher': 'Dispatcher',
    'team.role.clerk': 'Clerk',
    'team.role.finance': 'Finance',
    'team.role.driver': 'Driver',
    'team.department.operations': 'Operations',
    'team.department.dispatch': 'Dispatch',
    'team.department.finance': 'Finance',
    'team.department.administration': 'Administration',
    'team.workloadOverview': 'Workload Overview',
    'team.assignmentsToday': 'Assignments Today',
    'team.documentsProcessed': 'Documents Processed',
    'team.invoicesProcessed': 'Invoices Processed',
    'team.topDriverRanking': 'Top Driver Ranking',
    'team.completedDeliveriesToday': 'Completed Deliveries Today',
    'team.deliveries': 'Deliveries',
    'team.noDeliveriesToday': 'No completed deliveries recorded today.',
    'team.officeAttendance': 'Office Attendance',
    'team.adminSubtitle': 'Server-confirmed attendance and employee access.',
    'team.readonlySubtitle': 'Read-only attendance view for operational awareness.',
    'team.addEmployee': 'Add Employee',
    'team.addEmployeeLocked': 'Office verification is required before employee changes.',
    'team.trustLabel': 'Attendance trust status',
    'team.encryptedGateway': 'Encrypted server gateway',
    'team.serverTime': 'Server-confirmed time',
    'team.networkNeedsOffice': 'Needs Office Registration',
    'team.networkRegistered': 'Office Network Registered',
    'team.registerOffice': 'Register at Office',
    'team.registerDisabledAdmin': 'Office network registration is not enabled for this environment.',
    'team.registerDisabledRole': 'Only Admin can register the office network.',
    'team.summaryLabel': 'Attendance summary',
    'team.employees': 'Employees',
    'team.activeCount': '{{count}} active',
    'team.presentToday': 'Present Today',
    'team.serverConfirmed': 'Server confirmed',
    'team.notCheckedIn': 'Not Checked In',
    'team.activeEmployees': 'Active employees',
    'team.inactive': 'Inactive',
    'team.accessDisabled': 'Access disabled',
    'team.roster': 'Roster',
    'team.peopleCount': '{{count}} people',
    'team.today': 'Today',
    'team.serverConfirmedToday': 'Server-confirmed today',
    'team.noEmployees': 'No employees yet.',
    'team.noClockIns': 'No confirmed clock-ins yet.',
    'team.noClockIn': 'No clock-in yet',
    'team.lastRecord': 'Last server record',
    'team.employeeManagement': 'Employee management',
    'team.password': 'Password',
    'team.disable': 'Disable',
    'team.enable': 'Enable',
    'team.close': 'Close',
    'team.done': 'Done',
    'team.employee': 'Employee',
    'team.newAccess': 'New attendance access',
    'team.name': 'Name',
    'team.initialPassword': 'Initial password',
    'team.signInHelp': 'The employee signs in using this unique name and password.',
    'team.resetPassword': 'Reset password',
    'team.newPassword': 'New password',
    'team.oldPasswordStops': 'The old password stops working after Done.',
    'team.enableEmployee': 'Enable employee',
    'team.disableEmployee': 'Disable employee',
    'team.enableConfirm': 'Allow this employee to use attendance again?',
    'team.disableConfirm': 'Stop this employee from using attendance until re-enabled?',
    'team.mutationError': 'Attendance could not complete this request.',
    'team.loadError': 'Attendance is temporarily unavailable.',
    'team.updated': 'Attendance updated.',
    'settings.systemAdmin': 'System administration',
    'settings.title': 'Settings',
    'settings.subtitle': 'Manage access and review permanent system history.',
    'settings.searchUsers': 'Search users',
    'settings.nameOrEmail': 'Name or email',
    'settings.identity': 'Identity',
    'settings.accessRoles': 'Access & Roles',
    'settings.addCustomer': 'Add Customer',
    'settings.addDriver': 'Add Driver',
    'settings.userCount': '{{count}} users',
    'settings.noLoginEmail': 'No login email',
    'settings.accountLink': 'Account link · {{path}}',
    'settings.active': 'Active',
    'settings.inactive': 'Inactive',
    'settings.role': 'Role',
    'settings.roleFor': 'Role for {{name}}',
    'settings.driverRetired': 'Driver (retired)',
    'settings.noMatches': 'No users match this search.',
    'settings.permanentHistory': 'Permanent history',
    'settings.auditTitle': 'Immutable Audit',
    'settings.readOnly': 'Read only',
    'settings.accessChange': 'Access change',
    'settings.confirmRole': 'Confirm role update',
    'settings.guidance': 'After this update, the changed user must sign out and sign back in once.',
    'settings.cancel': 'Cancel',
    'settings.update': 'Update',
    'settings.thisUser': 'This user',
    'settings.roleChangeSummary': '{{name}}: {{from}} to {{to}}',
    'settings.roleUpdateFailed': 'Role update failed.',
    'settings.role.admin': 'Admin',
    'settings.role.dispatcher': 'Dispatcher',
    'settings.role.clerk': 'Clerk',
    'settings.role.finance': 'Finance',
    'settings.role.customer': 'Customer',
    'settings.role.driver': 'Driver',
    'settings.role.user': 'User',
    'status.title': 'Status',
    'status.dateRange': 'Status date range',
    'status.range.7d': '7D',
    'status.range.30d': '30D',
    'status.range.90d': '90D',
    'status.range.ytd': 'YTD',
    'status.kpiLabel': 'Status key performance indicators',
    'status.throughput': 'Throughput',
    'status.active': 'Active',
    'status.completed': 'Completed',
    'status.atRisk': 'At risk',
    'status.activityDensity': 'Activity density',
    'status.activityDensityAria': 'Activity density by interval',
    'status.heatCellTitle': '{{label}}: {{value}} · {{date}}',
    'status.containerMovement': 'Container movement',
    'status.movementAria': 'Container throughput, completion and at risk trend',
    'status.inspectTimeline': 'Inspect status timeline',
    'status.noData': 'No data',
    'status.completion': 'Completion',
    'status.riskShare': 'Risk share',
    'status.actionRequired': 'Action required',
    'status.revenue': 'Revenue',
    'status.cost': 'Cost',
    'status.profit': 'Profit',
    'status.revenueMovement': 'Revenue movement',
    'status.financeAria': 'Revenue, cost and profit trend',
    'status.profitMargin': 'Profit margin',
    'status.costShare': 'Cost share',
    'status.exceptionExposure': 'Exception exposure',
    'status.employeeStatus': 'Employee status',
    'status.present': 'Present',
    'status.notCheckedIn': 'Not checked in',
    'status.inactive': 'Inactive',
    'status.presentAria': '{{rate}} of active employees present',
    'status.attendanceDistribution': 'Employee attendance distribution',
    'status.waiting': 'Waiting',
    'status.appointmentRisk': 'Appointment risk',
    'status.returnBottleneck': 'Return bottleneck',
    'status.driverNeeded': 'Driver needed',
    'status.missingEta': 'Missing ETA',
    'status.missingLfd': 'Missing LFD',
    'status.documentReady': 'Document ready',
    'status.operationalBlockers': 'Operational blockers',
    'status.monthlyTrend': '{{label}} monthly trend',
    'taskWorkspace.restoreHidden': 'Restore hidden task windows',
    'taskWorkspace.restoreOne': 'Restore 1 hidden task window',
    'taskWorkspace.restoreMany': 'Restore {{count}} hidden task windows',
    'taskWorkspace.lock': 'Lock task window',
    'taskWorkspace.unlock': 'Unlock task window',
    'taskWorkspace.discard': 'Discard and close task',
    'taskWorkspace.hide': 'Temporarily hide task window',
    'taskWorkspace.done': 'Done',
    'taskWorkspace.task': 'Task',
    'taskWorkspace.window': 'Task window',
    'taskWorkspace.loading': 'Loading task…',
    'taskWorkspace.unavailable': 'Task unavailable',
    'taskWorkspace.openFailed': 'The task could not be opened.',
  },
  'zh-CN': {
    'meta.englishFallback': 'English fallback',
    'language.label': '语言',
    'language.english': 'EN',
    'language.chinese': '中文',
    'language.notSaved': '已在本设备切换语言,但未能保存到你的账号。',
    'common.loadingDashboard': '正在加载 TMS 仪表板...',
    'common.search': '搜索',
    'common.filter': '筛选',
    'common.total': '总计',
    'common.back': '返回',
    'common.retry': '重试',
    'loading.containers': '正在加载集装箱',
    'loading.containerDetail': '正在加载集装箱详情',
    'loading.failed': '无法加载所需数据。',
    'weather.currentAria': '长滩当前天气',
    'weather.loading': '正在加载天气',
    'weather.unavailable': '天气暂不可用',
    'weather.stale': '已保存 · {{condition}}',
    'weather.label': '天气',
    'weather.clear': '晴朗',
    'weather.partlyCloudy': '局部多云',
    'weather.overcast': '阴天',
    'weather.fog': '有雾',
    'weather.rain': '有雨',
    'weather.snow': '有雪',
    'weather.thunderstorms': '雷暴',
    'login.panelLabel': 'KM 登录',
    'login.product': '运输管理系统',
    'login.accountEmail': '账户邮箱',
    'login.password': '密码',
    'login.submit': '登录',
    'login.welcomeBack': '欢迎回来',
    'login.continue': '继续',
    'login.showPassword': '显示密码',
    'login.hidePassword': '隐藏密码',
    'login.invalidCredentials': '邮箱或密码无效。',
    'login.accountDisabled': '此账户已停用，请联系管理员。',
    'login.tooManyAttempts': '尝试次数过多，请稍后重试。',
    'login.networkError': '无法连接 Firebase。请检查网络后重试。',
    'login.serviceError': '登录服务暂时不可用，请重试。',
    'login.noRole': '您的账户尚未分配工作区，请联系管理员。',
    'login.sessionError': '登录成功，但无法加载账户权限。请重试。',
    'nav.dashboard': '仪表板',
    'nav.containers': '集装箱',
    'nav.addContainer': '添加集装箱',
    'nav.shipment': '货运',
    'nav.customers': '客户',
    'nav.drivers': '司机',
    'nav.status': '状态',
    'nav.companies': '客户总览',
    'nav.team': '团队',
    'nav.chassis': '车架',
    'nav.customerQuotes': '报价地址',
    'nav.settings': '设置',
    'nav.add': '添加',
    'nav.addCustomer': '添加客户',
    'nav.addDriver': '添加司机',
    'nav.customerPortal': '客户门户',
    'nav.addTask': '添加任务',
    'nav.bulkAdd': '批量新增',
    'nav.actions': '待办操作',
    'customerDashboard.title': '\u6211\u7684\u8d27\u67dc',
    'customerDashboard.subtitle': '\u6309\u4f18\u5148\u7ea7\u663e\u793a\u4e0e\u60a8\u8d26\u6237\u5173\u8054\u7684\u516c\u5f00\u8fd0\u8f93\u8282\u70b9\u3002',
    'customerDashboard.loading': '\u6b63\u5728\u52a0\u8f7d\u8d27\u67dc\u4eea\u8868\u677f\u2026',
    'customerDashboard.empty': '\u5f53\u524d\u6ca1\u6709\u53ef\u663e\u793a\u7684\u8d27\u67dc\u3002',
    'customerDashboard.error': '\u65e0\u6cd5\u52a0\u8f7d\u8d27\u67dc\u4eea\u8868\u677f\uff0c\u7cfb\u7edf\u672a\u663e\u793a\u4efb\u4f55\u79c1\u5bc6\u8fd0\u8425\u6570\u636e\u3002',
    'customerDashboard.retry': '\u91cd\u8bd5',
    'customerDashboard.refresh': '\u5237\u65b0',
    'customerDashboard.refreshWarning': '\u5237\u65b0\u5931\u8d25\uff0c\u5df2\u52a0\u8f7d\u7684\u5b89\u5168\u4fe1\u606f\u4ecd\u4fdd\u7559\u663e\u793a\u3002',
    'customerDashboard.consistency': '\u8fd0\u8425\u4eba\u5458\u8bb0\u5f55\u8282\u70b9\u540e\uff0c\u66f4\u65b0\u53ef\u80fd\u9700\u8981\u77ed\u6682\u65f6\u95f4\u624d\u4f1a\u663e\u793a\u3002',
    'customerDashboard.completionRule': '\u91cd\u67dc\u5df2\u9001\u8fbe\u53ea\u662f\u4e2d\u95f4\u8282\u70b9\uff0c\u53ea\u6709\u7a7a\u67dc\u5df2\u5f52\u8fd8\u624d\u4ee3\u8868 Container \u5b8c\u6210\u3002',
    'customerDashboard.moreUnavailable': '\u53ef\u80fd\u8fd8\u6709\u66f4\u591a\u8bb0\u5f55\uff0c\u4f46\u5b89\u5168\u5206\u9875\u6682\u65f6\u4e0d\u53ef\u7528\u3002',
    'customerDashboard.pagination': '\u52a0\u8f7d\u66f4\u591a\u8d27\u67dc\u8bb0\u5f55',
    'customerDashboard.more.operational': '\u52a0\u8f7d\u66f4\u591a\u8fd0\u8f93\u4e2d\u8d27\u67dc',
    'customerDashboard.more.due': '\u52a0\u8f7d\u66f4\u591a\u5373\u5c06\u5230\u671f\u8d27\u67dc',
    'customerDashboard.more.completed': '\u52a0\u8f7d\u66f4\u591a\u5df2\u5b8c\u6210\u8d27\u67dc',
    'customerDashboard.notProvided': '\u672a\u63d0\u4f9b',
    'customerDashboard.count': '{{count}} \u4e2a\u8d27\u67dc',
    'customerDashboard.groupEmpty': '\u6b64\u4f18\u5148\u7ea7\u5206\u7ec4\u6682\u65e0\u8d27\u67dc\u3002',
    'customerDashboard.group.attention': '\u9700\u8981\u5173\u6ce8',
    'customerDashboard.group.in_progress': '\u8fd0\u8f93\u4e2d',
    'customerDashboard.group.upcoming': '\u5373\u5c06\u5f00\u59cb',
    'customerDashboard.group.completed_recent': '\u6700\u8fd1\u5b8c\u6210',
    'customerDashboard.viewDetail': '\u67e5\u770b\u516c\u5f00\u8282\u70b9',
    'customerDashboard.detail.title': '\u8d27\u67dc\u8be6\u60c5',
    'customerDashboard.detail.eyebrow': '\u516c\u5f00\u8d27\u67dc\u8282\u70b9',
    'customerDashboard.detail.loading': '\u6b63\u5728\u52a0\u8f7d\u8d27\u67dc\u8be6\u60c5\u2026',
    'customerDashboard.detail.error': '\u8be5\u8d27\u67dc\u4e0d\u53ef\u7528\uff0c\u6216\u672a\u4e0e\u60a8\u7684\u8d26\u6237\u5173\u8054\u3002',
    'customerDashboard.field.dcContainer': 'DC \u8d27\u67dc',
    'customerDashboard.field.job': '\u5de5\u4f5c\u5355',
    'customerDashboard.field.terminal': '\u7801\u5934',
    'customerDashboard.field.ssl': '\u8239\u516c\u53f8',
    'customerDashboard.field.sizeType': '\u5c3a\u5bf8 / \u7c7b\u578b',
    'customerDashboard.field.status': '\u72b6\u6001',
    'customerDashboard.field.stage': '\u5f53\u524d\u9636\u6bb5',
    'customerDashboard.field.eta': '\u9884\u8ba1\u5230\u8fbe',
    'customerDashboard.field.lfd': '\u514d\u5806\u671f\u622a\u6b62',
    'customerDashboard.field.due': '\u5230\u671f',
    'customerDashboard.field.pickupAppointment': '\u63d0\u67dc\u9884\u7ea6',
    'customerDashboard.field.deliveryAppointment': '\u9001\u8fbe\u9884\u7ea6',
    'customerDashboard.field.loadOut': '\u51fa\u67dc',
    'customerDashboard.field.loadedDelivered': '\u91cd\u67dc\u5df2\u9001\u8fbe',
    'customerDashboard.field.emptyReady': '\u7a7a\u67dc\u5df2\u51c6\u5907',
    'customerDashboard.field.emptyPickedUp': '\u7a7a\u67dc\u5df2\u63d0\u53d6',
    'customerDashboard.field.emptyReturned': '\u7a7a\u67dc\u5df2\u5f52\u8fd8',
    'customerDashboard.status.pending': '\u5f85\u5904\u7406',
    'customerDashboard.status.dispatched': '\u5df2\u6d3e\u8f66',
    'customerDashboard.status.in_progress': '\u8fd0\u8f93\u4e2d',
    'customerDashboard.status.loaded_delivered': '\u91cd\u67dc\u5df2\u9001\u8fbe',
    'customerDashboard.status.empty_ready': '\u7a7a\u67dc\u5df2\u51c6\u5907',
    'customerDashboard.status.returning_empty': '\u6b63\u5728\u5f52\u8fd8\u7a7a\u67dc',
    'customerDashboard.status.empty_returned': '\u7a7a\u67dc\u5df2\u5f52\u8fd8',
    'customerDashboard.status.picked_up_from_terminal': '\u5df2\u4ece\u7801\u5934\u63d0\u67dc',
    'customerDashboard.status.arrived_at_warehouse': '\u5df2\u5230\u4ed3',
    'customerDashboard.status.out_for_delivery': '\u6b63\u5728\u9001\u8fbe',
    'nav.tasks': '任务',
    'nav.users': '用户',
    'shell.noRole': '无角色',
    'shell.logout': '退出登录',
    'shell.profile': '个人信息',
    'shell.openNavigation': '打开导航',
    'shell.closeNavigation': '关闭导航',
    'shell.primaryNavigation': '主导航',
    'shell.noRoleAssigned': '未分配角色。请联系管理员。',
    'dispatch.edit.open': '编辑当前任务',
    'dispatch.edit.title': '编辑 Dispatch Task',
    'dispatch.edit.notice': '时间、路线和调度备注的更改都会记录到 Audit Log。',
    'dispatch.edit.scheduledStart': '计划开始时间 *',
    'dispatch.edit.scheduledEnd': '计划结束时间',
    'dispatch.edit.originName': '起点名称',
    'dispatch.edit.originAddress': '起点地址',
    'dispatch.edit.originType': '起点类型',
    'dispatch.edit.destinationName': '终点名称',
    'dispatch.edit.destinationAddress': '终点地址',
    'dispatch.edit.destinationType': '终点类型',
    'dispatch.edit.notes': '调度备注',
    'dispatch.edit.save': '保存任务修改',
    'dispatch.edit.saving': '正在保存…',
    'dispatch.edit.success': 'Dispatch Task 已更新。',
    'dispatch.point.terminal': '码头',
    'dispatch.point.yard': '堆场',
    'dispatch.point.customer': '顾客',
    'dispatch.edit.error.EDIT_PERMISSION_DENIED': '您没有权限编辑此 Dispatch Task。',
    'dispatch.edit.error.EDIT_TASK_NOT_FOUND': '此 Dispatch Task 已不可用。',
    'dispatch.edit.error.EDIT_REQUEST_INVALID': '保存前请检查时间、路线和备注。',
    'dispatch.edit.error.EDIT_STATE_CHANGED': '编辑期间任务已经变化，请刷新后重新检查。',
    'dispatch.edit.error.EDIT_TERMINAL_TASK': '已完成、已取消或失败的任务不能再编辑。',
    'dispatch.edit.error.EDIT_ROUTE_STATE_INVALID': '司机开始路线后，不能再修改计划时间或路线。',
    'dispatch.edit.error.EDIT_REQUEST_CONFLICT': '本次编辑请求与先前请求冲突，请刷新后重试。',
    'dispatch.edit.error.EDIT_WRITE_FAILED': '尚未确认修改成功，请重试同一请求或刷新任务。',
    'dispatch.progress.accept': '接受任务',
    'dispatch.progress.start': '开始路线',
    'dispatch.progress.arrived': '已到达',
    'dispatch.progress.complete': '完成运输任务',
    'dispatch.progress.failed': '标记任务失败',
    'dispatch.progress.updated': '任务进度已更新。',
    'dispatch.progress.notice': '这些按钮只更新您的 Dispatch Task。运输任务完成不代表 Container 完成；只有空柜已归还（Empty Returned）才算完成。',
    'error.somethingWrong': '出现错误',
    'dispatch.title': '正式派车',
    'dispatch.authorityNotice': '此操作将创建正式 Dispatch Task。Container 中的司机文字不能证明已经派车。',
    'dispatch.driver': '可派车司机',
    'dispatch.selectDriver': '选择可派车司机',
    'dispatch.legType': '运输阶段',
    'dispatch.scheduledStart': '计划开始时间 *',
    'dispatch.confirm': '确认并创建任务',
    'dispatch.creating': '正在创建任务…',
    'dispatch.success': '正式 Dispatch Task 已创建。',
    'dispatch.notOfficiallyDispatched': '未正式派车',
    'dispatch.dataReview': '派车数据需要检查',
    'dispatch.taskCancelledNotDispatched': '任务已取消 — 未正式派车',
    'dispatch.taskFailedNotDispatched': '任务失败 — 未正式派车',
    'dispatch.taskCompletedNoActiveDriver': '运输任务已完成 — 当前无在途司机',
    'dispatch.noEligibleDrivers': '当前没有通过服务器验证的可派车司机账号。',
    'dispatch.driverListError': '无法加载可派车司机，请刷新后重试。',
    'dispatch.searchDrivers': '按姓名搜索可派车司机',
    'dispatch.driverListTruncated': '还有更多匹配司机，请缩小姓名搜索范围以找到正确账号。',
    'dispatch.nextDrivers': '下一页司机结果',
    'dispatch.statusLoadError': '无法加载派车状态，请刷新后重试。',
    'dispatch.anomalyTitle': '派车数据异常',
    'dispatch.activeConflictTitle': '已有进行中的 Dispatch Task',
    'dispatch.viewCurrentTask': '查看当前任务',
    'dispatch.cancel.historyWarning': '当前任务将被取消，不会被删除。任务历史和 Audit Log 将继续保留。替代任务需要另行创建。',
    'dispatch.cancel.reason': '取消原因 *',
    'dispatch.cancel.selectReason': '选择取消原因',
    'dispatch.cancel.explanation': '其他原因说明 *',
    'dispatch.cancel.confirm': '确认取消当前任务',
    'dispatch.cancel.cancelling': '正在取消任务…',
    'dispatch.cancel.retry': '重试取消操作',
    'dispatch.cancel.retrying': '正在确认取消结果…',
    'dispatch.cancel.success': '原任务已取消',
    'dispatch.cancel.separateAssignment': '系统没有自动创建替代任务。准备好后再继续执行独立的正式派车。',
    'dispatch.cancel.continue': '继续新的正式派车',
    'dispatch.cancel.notAuthorized': '只有获得授权的 Admin 或 Dispatcher 可以取消此任务。',
    'dispatch.cancel.error.CANCELLATION_PERMISSION_DENIED': '您没有权限取消此 Dispatch Task。',
    'dispatch.cancel.error.CANCELLATION_TASK_NOT_FOUND': '此 Dispatch Task 已不可用，请刷新后再继续。',
    'dispatch.cancel.error.CANCELLATION_REASON_INVALID': '请选择取消原因。选择其他原因时，必须填写 1 至 500 个字符的说明。',
    'dispatch.cancel.error.CANCELLATION_NOT_ACTIVE': '此任务已不再进行中，无法取消。',
    'dispatch.cancel.error.CANCELLATION_DATA_ANOMALY': '派车数据需要管理员检查，任务尚未取消。',
    'dispatch.cancel.error.CANCELLATION_STATE_CHANGED': '操作期间任务已经变化，请刷新并检查当前任务。',
    'dispatch.cancel.error.CANCELLATION_REQUEST_CONFLICT': '本次取消请求与之前的请求冲突，请刷新后重试。',
    'dispatch.cancel.error.CANCELLATION_WRITE_FAILED': '取消操作尚未确认成功，请刷新任务状态后再试。',
    'dispatch.error.ASSIGNMENT_ACTIVE_CONFLICT': '已存在进行中的 Dispatch Task。请先查看或取消当前任务。',
    'dispatch.error.ASSIGNMENT_DATA_ANOMALY': '派车数据需要管理员检查，当前不能创建新任务。',
    'dispatch.error.ASSIGNMENT_DRIVER_NOT_ELIGIBLE': '此司机不符合派车资格，请选择其他有效司机。',
    'dispatch.error.ASSIGNMENT_DRIVER_ACCOUNT_NOT_LINKED': '此司机尚未绑定登录账号，无法接收任务。请先为该司机绑定账号，或选择其他司机。',
    'dispatch.error.ASSIGNMENT_SCHEDULE_REQUIRED': '请选择计划开始日期和时间。',
    'dispatch.error.ASSIGNMENT_SCHEDULE_IN_PAST': '计划开始时间不能早于服务器时间超过 5 分钟。',
    'dispatch.error.ASSIGNMENT_PERMISSION_DENIED': '您没有权限执行此派车操作。',
    'dispatch.error.ASSIGNMENT_CONTAINER_NOT_FOUND': '此 Container 已不可用，请返回列表并刷新。',
    'dispatch.error.ASSIGNMENT_NEXT_LEG_INVALID': '所选运输阶段已经无效，请刷新 Container 后再继续。',
    'dispatch.error.ASSIGNMENT_STATE_CHANGED': '操作期间派车状态已经变化，请先查看最新状态。',
    'dispatch.error.ASSIGNMENT_WRITE_FAILED': '无法创建任务，当前未确认成功。请刷新派车状态后再试。',
    'test.welcome': '欢迎，{{name}}！',
    'dashboard.title': '工作台',
    'dashboard.inactive': '未启用',
    'dashboard.active': '运输中',
    'dashboard.urgent': '紧急',
    'dashboard.dueToday': '今日到期',
    'dashboard.kpi.total': 'Total Containers',
    'dashboard.kpi.active': 'Active Containers',
    'dashboard.kpi.risk': 'At Risk',
    'dashboard.kpi.completed7d': 'Completed (7d)',
    'dashboard.kpi.revenue7d': '收入（7 天）',
    'dashboard.kpi.dueToday': 'Due Today',
    'dashboard.kpi.vsLastMonth': '与上月相比',
    'dashboard.kpi.trend': '{{label}} monthly trend',
    'dashboard.emptyReturned': '已还空',
    'dashboard.monthStatus': '本月状态',
    'dashboard.completionHealth': '完成情况',
    'dashboard.stageProgress': '阶段进度',
    'dashboard.emptyReturnedOnTime': '按时还空',
    'dashboard.stagePickedUp': '已提柜',
    'dashboard.stageWarehouseArrived': '已到仓库',
    'dashboard.stageReturned': '已还空',
    'dashboard.tableTitle': '工作台集装箱',
    'dashboard.noContainers': '未找到集装箱。',
    'dashboard.container': '集装箱',
    'dashboard.dcContainer': 'DC 集装箱',
    'dashboard.job': '工作单',
    'dashboard.customer': '客户',
    'dashboard.customerLink': '客户关联',
    'dashboard.terminal': '码头',
    'dashboard.ssl': '船公司',
    'dashboard.eta': '预计到达',
    'dashboard.lfd': '免租截止',
    'dashboard.due': '到期时间',
    'dashboard.pickupAppointment': '提柜预约',
    'dashboard.deliveryAppointment': '送达预约',
    'dashboard.status': '状态',
    'dashboard.driver': '司机',
    'dashboard.lastNote': '最新备注',
    'dashboard.charges': '费用',
    'dashboard.quickStatus': '快速状态',
    'dashboard.edit': '编辑',
    'dashboard.unassigned': '未分配',
    'dashboard.yes': '是',
    'dashboard.locked': '已锁定',
    'dashboard.editContainer': '编辑集装箱',
    'admin.users.title': '管理员用户',
    'admin.users.roleNotice': '角色更改会更新 Firebase 自定义声明。该用户必须退出后重新登录。',
    'admin.users.name': '姓名',
    'admin.users.email': '邮箱',
    'admin.users.role': '角色',
    'admin.users.customerUid': '客户 UID',
    'admin.users.customerRef': '客户引用',
    'admin.users.active': '启用',
    'admin.users.yes': '是',
    'admin.users.no': '否',
    'admin.users.roleUpdated': '角色已更新。用户必须退出后重新登录。',
    'admin.users.roleUpdateFailed': '角色更新失败。',
    'audit.title': '管理员日志',
    'audit.subtitle': '不可更改的业务操作记录。每页加载 100 条。',
    'audit.pageSize': '每页 100 条记录',
    'audit.startDate': '开始日期',
    'audit.startTime': '开始时间',
    'audit.endDate': '结束日期',
    'audit.endTime': '结束时间',
    'audit.search': '搜索管理员日志',
    'audit.clear': '清除',
    'audit.time': '时间',
    'audit.actor': '操作人',
    'audit.item': '更改项目',
    'audit.summary': '摘要',
    'audit.details': '详情',
    'audit.older': '更早',
    'audit.newer': '更新',
    'audit.pagination': '管理员日志分页',
    'audit.loading': '正在加载管理员日志...',
    'audit.empty': '所选范围内没有业务日志。',
    'audit.collection': '集合',
    'audit.documentId': '文档 ID',
    'audit.operation': '操作',
    'audit.role': '角色',
    'audit.eventTime': '事件时间',
    'audit.changedFields': '更改字段',
    'audit.previous': '原值',
    'audit.next': '新值',
    'audit.reason': '原因',
    'audit.logId': '日志 ID',
    'audit.technical': '技术详情',
    'audit.showDetails': '显示 {{item}} 的详情',
    'audit.hideDetails': '隐藏 {{item}} 的详情',
    'audit.loadError': '无法加载管理员日志。',
    'audit.invalidRange': '结束时间必须晚于开始时间。',
    'audit.missingRange': '请选择完整的开始和结束日期时间。',
    'audit.longRange': '搜索范围超过 90 天，可能需要加载多个页面。是否继续？',
    'audit.newEvents': '{{count}} 条新管理员日志',
    'audit.newEvent': '1 条新管理员日志',
    'team.title': '团队',
    'team.operationalSummary': '团队运营概览',
    'team.operationsDirectory': '运营团队目录',
    'team.totalEmployees': '员工总数',
    'team.activeToday': '今日活跃',
    'team.onDuty': '正在值班',
    'team.onLeave': '休假中',
    'team.vsLast7Days': '较过去 7 天',
    'team.sevenDayTrend': '7 天趋势',
    'team.trendUnavailable': '—',
    'team.members': '团队成员',
    'team.memberCount': '{{count}} 名成员',
    'team.showingAllMembers': '显示全部 {{count}} 名成员',
    'team.employeeName': '员工姓名',
    'team.employeeId': '员工 ID',
    'team.role': '角色',
    'team.department': '部门',
    'team.status': '状态',
    'team.lastActive': '最近活跃',
    'team.actions': '操作',
    'team.actionsFor': '{{name}} 的操作',
    'team.noMembers': '暂无团队成员。',
    'team.unnamedMember': '未命名成员',
    'team.status.active': '活跃',
    'team.status.offline': '离线',
    'team.status.onLeave': '休假中',
    'team.status.inactive': '已停用',
    'team.role.admin': '管理员',
    'team.role.dispatcher': '调度',
    'team.role.clerk': '文员',
    'team.role.finance': '财务',
    'team.role.driver': '司机',
    'team.department.operations': '运营',
    'team.department.dispatch': '调度',
    'team.department.finance': '财务',
    'team.department.administration': '行政',
    'team.workloadOverview': '工作量概览',
    'team.assignmentsToday': '今日分配',
    'team.documentsProcessed': '已处理文件',
    'team.invoicesProcessed': '已处理发票',
    'team.topDriverRanking': '司机排名',
    'team.completedDeliveriesToday': '今日完成运输',
    'team.deliveries': '运输单',
    'team.noDeliveriesToday': '今日暂无已完成运输。',
    'team.officeAttendance': '办公室考勤',
    'team.adminSubtitle': '服务器确认的考勤与员工访问管理。',
    'team.readonlySubtitle': '只读考勤视图，用于运营查看。',
    'team.addEmployee': '新增员工',
    'team.addEmployeeLocked': '更改员工信息前需要完成办公室验证。',
    'team.trustLabel': '考勤可信状态',
    'team.encryptedGateway': '加密服务器网关',
    'team.serverTime': '服务器确认时间',
    'team.networkNeedsOffice': '需要登记办公室网络',
    'team.networkRegistered': '办公室网络已登记',
    'team.registerOffice': '登记办公室网络',
    'team.registerDisabledAdmin': '当前环境未启用办公室网络登记。',
    'team.registerDisabledRole': '只有管理员可以登记办公室网络。',
    'team.summaryLabel': '考勤汇总',
    'team.employees': '员工',
    'team.activeCount': '{{count}} 人启用',
    'team.presentToday': '今日已打卡',
    'team.serverConfirmed': '服务器已确认',
    'team.notCheckedIn': '未打卡',
    'team.activeEmployees': '启用员工',
    'team.inactive': '已停用',
    'team.accessDisabled': '访问已停用',
    'team.roster': '员工名单',
    'team.peopleCount': '{{count}} 人',
    'team.today': '今日',
    'team.serverConfirmedToday': '今日服务器已确认',
    'team.noEmployees': '暂无员工。',
    'team.noClockIns': '今日暂无已确认打卡。',
    'team.noClockIn': '尚无打卡记录',
    'team.lastRecord': '最近服务器记录',
    'team.employeeManagement': '员工管理',
    'team.password': '密码',
    'team.disable': '停用',
    'team.enable': '启用',
    'team.close': '关闭',
    'team.done': '完成',
    'team.employee': '员工',
    'team.newAccess': '新考勤账号',
    'team.name': '姓名',
    'team.initialPassword': '初始密码',
    'team.signInHelp': '员工使用唯一姓名和密码登录。',
    'team.resetPassword': '重置密码',
    'team.newPassword': '新密码',
    'team.oldPasswordStops': '点击完成后旧密码将停止使用。',
    'team.enableEmployee': '启用员工',
    'team.disableEmployee': '停用员工',
    'team.enableConfirm': '允许此员工重新使用考勤系统？',
    'team.disableConfirm': '暂停此员工使用考勤系统，直到重新启用？',
    'team.mutationError': '考勤请求未能完成。',
    'team.loadError': '考勤服务暂时不可用。',
    'team.updated': '考勤信息已更新。',
    'settings.systemAdmin': '系统管理',
    'settings.title': '设置',
    'settings.subtitle': '管理访问权限并查看永久系统记录。',
    'settings.searchUsers': '搜索用户',
    'settings.nameOrEmail': '姓名或邮箱',
    'settings.identity': '身份',
    'settings.accessRoles': '访问权限与角色',
    'settings.addCustomer': '添加客户',
    'settings.addDriver': '添加司机',
    'settings.userCount': '{{count}} 位用户',
    'settings.noLoginEmail': '没有登录邮箱',
    'settings.accountLink': '账号关联 · {{path}}',
    'settings.active': '启用',
    'settings.inactive': '停用',
    'settings.role': '角色',
    'settings.roleFor': '{{name}} 的角色',
    'settings.driverRetired': '司机（已停用）',
    'settings.noMatches': '没有符合搜索条件的用户。',
    'settings.permanentHistory': '永久记录',
    'settings.auditTitle': '不可更改的管理员日志',
    'settings.readOnly': '只读',
    'settings.accessChange': '访问权限更改',
    'settings.confirmRole': '确认角色更新',
    'settings.guidance': '更新后，该用户需要退出并重新登录一次。',
    'settings.cancel': '取消',
    'settings.update': '更新',
    'settings.thisUser': '此用户',
    'settings.roleChangeSummary': '{{name}}：从{{from}}改为{{to}}',
    'settings.roleUpdateFailed': '角色更新失败。',
    'settings.role.admin': '管理员',
    'settings.role.dispatcher': '调度',
    'settings.role.clerk': '文员',
    'settings.role.finance': '财务',
    'settings.role.customer': '客户',
    'settings.role.driver': '司机',
    'settings.role.user': '用户',
    'status.title': '状态分析',
    'status.dateRange': '状态日期范围',
    'status.range.7d': '7天',
    'status.range.30d': '30天',
    'status.range.90d': '90天',
    'status.range.ytd': '今年',
    'status.kpiLabel': '状态关键指标',
    'status.throughput': '处理量',
    'status.active': '进行中',
    'status.completed': '已完成',
    'status.atRisk': '有风险',
    'status.activityDensity': '活动密度',
    'status.activityDensityAria': '各时间段活动密度',
    'status.heatCellTitle': '{{date}} · {{label}}：{{value}}',
    'status.containerMovement': '集装箱流转',
    'status.movementAria': '集装箱处理量、完成量与风险趋势',
    'status.inspectTimeline': '查看状态时间线',
    'status.noData': '暂无数据',
    'status.completion': '完成率',
    'status.riskShare': '风险占比',
    'status.actionRequired': '需要处理',
    'status.revenue': '营收',
    'status.cost': '成本',
    'status.profit': '利润',
    'status.revenueMovement': '营收走势',
    'status.financeAria': '营收、成本与利润趋势',
    'status.profitMargin': '利润率',
    'status.costShare': '成本占比',
    'status.exceptionExposure': '异常费用',
    'status.employeeStatus': '员工状态',
    'status.present': '已打卡',
    'status.notCheckedIn': '未打卡',
    'status.inactive': '已停用',
    'status.presentAria': '启用员工出勤率 {{rate}}',
    'status.attendanceDistribution': '员工出勤分布',
    'status.waiting': '待打卡',
    'status.appointmentRisk': '预约风险',
    'status.returnBottleneck': '还空瓶颈',
    'status.driverNeeded': '需要司机',
    'status.missingEta': '缺少 ETA',
    'status.missingLfd': '缺少 LFD',
    'status.documentReady': '单证就绪',
    'status.operationalBlockers': '运营阻塞项',
    'status.monthlyTrend': '{{label}}月度趋势',
    'taskWorkspace.restoreHidden': '恢复隐藏的任务窗口',
    'taskWorkspace.restoreOne': '恢复 1 个隐藏的任务窗口',
    'taskWorkspace.restoreMany': '恢复 {{count}} 个隐藏的任务窗口',
    'taskWorkspace.lock': '锁定任务窗口',
    'taskWorkspace.unlock': '解锁任务窗口',
    'taskWorkspace.discard': '放弃并关闭任务',
    'taskWorkspace.hide': '暂时隐藏任务窗口',
    'taskWorkspace.done': '完成',
    'taskWorkspace.task': '任务',
    'taskWorkspace.window': '任务窗口',
    'taskWorkspace.loading': '正在加载任务…',
    'taskWorkspace.unavailable': '任务不可用',
    'taskWorkspace.openFailed': '无法打开此任务。',
  },
};

export function translationCatalogKeys(locale) {
  const normalized = normalizeSupportedLocale(locale) || DEFAULT_LOCALE;
  return Object.keys(translations[normalized]).sort();
}

function normalizeSupportedLocale(locale) {
  const value = String(locale || '').trim().toLowerCase();
  if (value === 'zh' || value.startsWith('zh-')) return 'zh-CN';
  if (value === 'en' || value.startsWith('en-')) return 'en';
  return null;
}

function defaultStorage() {
  try {
    return globalThis.localStorage ?? null;
  } catch (_) {
    return null;
  }
}

function storedLocale(storage) {
  try {
    return normalizeSupportedLocale(storage?.getItem?.(STORAGE_KEY));
  } catch (_) {
    return null;
  }
}

// A marker is {locale, baseline}: the language the user picked, and the profile
// language observed when the save failed. `baseline` is the entire conflict
// rule -- no timestamps, no version numbers. See
// docs/contracts/_design-locale-offline-failure.md section 4.2.2.
export function readUnsavedLocale(storage = defaultStorage()) {
  let raw = null;
  try {
    raw = storage?.getItem?.(UNSAVED_KEY) ?? null;
  } catch (_) {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    const locale = normalizeSupportedLocale(parsed?.locale);
    if (!locale) throw new Error('unsupported');
    const baseline = normalizeSupportedLocale(parsed?.baseline);
    return { locale, baseline: baseline ?? null };
  } catch (_) {
    // Corrupt or unsupported markers must never wedge language selection.
    clearUnsavedLocale(storage);
    return null;
  }
}

export function writeUnsavedLocale({ locale, baseline = null } = {}, storage = defaultStorage()) {
  const normalized = normalizeSupportedLocale(locale);
  if (!normalized) return false;
  try {
    storage?.setItem?.(UNSAVED_KEY, JSON.stringify({
      locale: normalized,
      baseline: normalizeSupportedLocale(baseline) ?? null,
    }));
    return true;
  } catch (_) {
    return false;
  }
}

export function clearUnsavedLocale(storage = defaultStorage()) {
  try {
    storage?.removeItem?.(UNSAVED_KEY);
  } catch (_) {
    // Storage can be unavailable in private or embedded browser contexts.
  }
}

// The single place the "server wins" rule is implemented. The marker only holds
// while the profile has not moved since the failure: if it has, the preference
// was saved somewhere else and that value is the authoritative one.
export function resolveUnsavedLocale({ marker, profileLocale = null } = {}) {
  if (!marker) return null;
  const locale = normalizeSupportedLocale(marker.locale);
  if (!locale) return null;
  const baseline = normalizeSupportedLocale(marker.baseline) ?? null;
  const profile = normalizeSupportedLocale(profileLocale) ?? null;
  if (baseline !== profile) return null;
  return { locale, shouldRetry: true };
}

export function detectLocale({
  profileLocale = null,
  // Already validated by resolveUnsavedLocale. This function stays pure and
  // never reads the marker itself, so the conflict rule has one implementation.
  unsavedLocale = null,
  storage = defaultStorage(),
} = {}) {
  const unsaved = normalizeSupportedLocale(unsavedLocale);
  if (unsaved) return unsaved;

  const profile = normalizeSupportedLocale(profileLocale);
  if (profile) return profile;

  const persisted = storedLocale(storage);
  if (persisted) return persisted;

  // The system UI ships in English. The browser's language is deliberately not
  // consulted: a Chinese-locale browser used to flip the whole chrome to
  // Chinese for a user who never asked for it. Only an explicit choice -- the
  // unsaved marker, the saved profile, or the persisted preference -- moves it.
  return DEFAULT_LOCALE;
}

let currentLocale = detectLocale();
const localeSubscribers = new Set();
let localePreferenceRevision = 0;

function escapeInterpolation(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function interpolate(key, variables, locale, transform) {
  const normalized = normalizeSupportedLocale(locale) || DEFAULT_LOCALE;
  const template = translations[normalized]?.[key] ?? translations.en[key] ?? key;
  return template.replace(/\{\{([A-Za-z0-9_]+)\}\}/g, (_match, name) => (
    transform(variables[name])
  ));
}

export function t(key, variables = {}, locale = currentLocale) {
  return interpolate(key, variables, locale, (value) => String(value ?? ''));
}

export function tHtml(key, variables = {}, locale = currentLocale) {
  return interpolate(key, variables, locale, escapeInterpolation);
}

export function getLocale() {
  return currentLocale;
}

export function setLocale(locale, {
  persist = true,
  storage = defaultStorage(),
  source = 'user',
} = {}) {
  const nextLocale = normalizeSupportedLocale(locale) || DEFAULT_LOCALE;
  if (source === 'user') localePreferenceRevision += 1;
  if (persist) {
    try {
      storage?.setItem?.(STORAGE_KEY, nextLocale);
    } catch (_) {
      // Storage can be unavailable in private or embedded browser contexts.
    }
  }
  if (nextLocale === currentLocale) return currentLocale;
  currentLocale = nextLocale;
  for (const subscriber of [...localeSubscribers]) subscriber(currentLocale);
  return currentLocale;
}

export function getLocalePreferenceRevision() {
  return localePreferenceRevision;
}

export function shouldApplyProfileLocale({
  sessionPreferenceRevision,
  preferenceRevisionAtLoad,
  currentPreferenceRevision,
}) {
  return sessionPreferenceRevision === preferenceRevisionAtLoad
    && preferenceRevisionAtLoad === currentPreferenceRevision;
}

export function createSessionLoadCoordinator() {
  let generation = 0;
  let currentUid = null;

  function isCurrent(session) {
    return session?.generation === generation && session.uid === currentUid;
  }

  return {
    begin(uid, { preferenceRevision = 0 } = {}) {
      generation += 1;
      currentUid = typeof uid === 'string' && uid ? uid : null;
      return Object.freeze({
        generation,
        uid: currentUid,
        preferenceRevisionAtStart: preferenceRevision,
      });
    },
    isCurrent,
    async load(session, {
      getPreferenceRevision,
      loadToken,
      loadProfile,
    }) {
      if (!isCurrent(session) || !session.uid) return null;
      const preferenceRevisionAtLoad = getPreferenceRevision();
      const token = await loadToken();
      if (!isCurrent(session)) return null;
      const profile = await loadProfile(session.uid);
      if (!isCurrent(session)) return null;
      return { preferenceRevisionAtLoad, profile, token };
    },
  };
}

// A session that ended cannot be repaired by retrying, and a contract failure
// cannot be repaired by retrying either. Everything else is treated as
// transient, which is the dominant case once this write becomes an RPC.
function localeWriteFailure(error) {
  const code = String(error?.code ?? '').toLowerCase();
  if (code.includes('unauthenticated')) return { reason: 'unauthenticated', retryable: false };
  if (code.includes('permission-denied')) return { reason: 'permission-denied', retryable: false };
  return { reason: 'unavailable', retryable: true };
}

export async function persistLocalePreference({
  locale,
  baseline = null,
  write,
  onPersisted,
  onExhausted,
  isCurrent = () => true,
  storage = defaultStorage(),
  retryDelays = [1000, 4000, 10000],
  sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); }),
}) {
  const settle = (failure) => {
    // An unauthenticated session is handed back to the sign-in flow untouched:
    // marking and warning would just add noise to a re-login the user must do
    // anyway.
    if (failure.reason === 'unauthenticated') return false;
    writeUnsavedLocale({ locale, baseline }, storage);
    if (typeof onExhausted === 'function') onExhausted({ locale, reason: failure.reason });
    return false;
  };

  for (let attempt = 0; ; attempt += 1) {
    if (!isCurrent()) return false;
    try {
      await write(locale);
      clearUnsavedLocale(storage);
      if (typeof onPersisted === 'function') onPersisted(locale);
      return true;
    } catch (error) {
      const failure = localeWriteFailure(error);
      if (!failure.retryable || attempt >= retryDelays.length) {
        // A superseded choice is discarded silently: the newer one owns the
        // marker and the warning.
        if (!isCurrent()) return false;
        return settle(failure);
      }
      await sleep(retryDelays[attempt]);
    }
  }
}

export function subscribeLocale(subscriber) {
  if (typeof subscriber !== 'function') throw new TypeError('Locale subscriber must be a function.');
  localeSubscribers.add(subscriber);
  return () => localeSubscribers.delete(subscriber);
}

function dateFromValue(value) {
  if (value == null) return null;
  const date = typeof value?.toDate === 'function'
    ? value.toDate()
    : value instanceof Date ? value : new Date(value);
  return date instanceof Date && Number.isFinite(date.getTime()) ? date : null;
}

// `includeYear` defaults to true so every existing caller keeps the year. The
// Container table is the one surface that opts out: it is scanned column by
// column and the year is the same on effectively every row, so it costs width
// without telling the operator anything (Alan, 2026-07-31).
// Constructing an Intl formatter is expensive - it resolves locale data - and
// these three are called once per cell. A 410-row Container table with the admin
// column set was building on the order of ten thousand of them for a single
// paint, all of which collapse to at most six distinct shapes: two locales times
// three formats (with and without the year). They are built once and reused.
//
// Keyed on the exact arguments that change the output, so a new locale or a new
// option combination gets its own formatter rather than silently reusing one
// that formats differently. The cache is bounded by that key space, not by
// input volume, so it cannot grow with the number of rows.
const FORMATTER_CACHE = new Map();

function cachedFormatter(key, build) {
  let formatter = FORMATTER_CACHE.get(key);
  if (!formatter) {
    formatter = build();
    FORMATTER_CACHE.set(key, formatter);
  }
  return formatter;
}

function intlLocale(locale) {
  return (normalizeSupportedLocale(locale) || DEFAULT_LOCALE) === 'en' ? 'en-US' : 'zh-CN';
}

export function formatLocalizedDate(value, locale = currentLocale, { includeYear = true } = {}) {
  const date = dateFromValue(value);
  if (!date) return '';
  const tag = intlLocale(locale);
  return cachedFormatter(`dt:${tag}:${includeYear}`, () => new Intl.DateTimeFormat(tag, {
    timeZone: TIME_ZONE,
    ...(includeYear ? { year: 'numeric' } : {}),
    month: 'short',
    day: 'numeric',
    // 24-hour, zero padded, in every locale (Alan, 2026-08-11). en-US would
    // otherwise render "11:45 PM": three characters of suffix, and an hour column
    // that cannot be read down because 9:05 and 11:45 do not line up. h23 rather
    // than hour12:false - the latter can produce a 24:00 hour in some locales.
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    // No seconds. An appointment is booked to the minute, so the ":00" was two
    // characters of noise in every date-time column (Alan, 2026-08-10). The audit
    // log keeps its seconds - it has its own formatter in audit-utils.mjs,
    // because there the exact instant is the record.
  })).format(date);
}

export function formatLocalizedDateOnly(value, locale = currentLocale, { includeYear = true } = {}) {
  const date = dateFromValue(value);
  if (!date) return '';
  const tag = intlLocale(locale);
  return cachedFormatter(`d:${tag}:${includeYear}`, () => new Intl.DateTimeFormat(tag, {
    timeZone: TIME_ZONE,
    ...(includeYear ? { year: 'numeric' } : {}),
    month: 'short',
    day: 'numeric',
  })).format(date);
}

export function formatLocalizedCurrency(cents, locale = currentLocale) {
  const numericCents = Number(cents);
  const integerCents = Number.isFinite(numericCents) ? Math.round(numericCents) : 0;
  const tag = intlLocale(locale);
  return cachedFormatter(`c:${tag}`, () => new Intl.NumberFormat(tag, {
    style: 'currency',
    currency: 'USD',
  })).format(integerCents / 100);
}

function variablesForElement(element) {
  const value = element?.dataset?.i18nVariables;
  if (!value) return {};
  try {
    const variables = JSON.parse(value);
    return variables && typeof variables === 'object' && !Array.isArray(variables) ? variables : {};
  } catch (_) {
    return {};
  }
}

export function localizeDom(root = globalThis.document, locale = currentLocale) {
  if (!root?.querySelectorAll) return;
  const normalized = normalizeSupportedLocale(locale) || DEFAULT_LOCALE;
  const documentElement = root.documentElement ?? root.ownerDocument?.documentElement;
  if (documentElement) documentElement.lang = normalized;

  root.querySelectorAll('[data-i18n]').forEach((element) => {
    element.textContent = t(element.dataset.i18n, variablesForElement(element), normalized);
  });
  root.querySelectorAll('[data-i18n-placeholder]').forEach((element) => {
    element.setAttribute('placeholder', t(
      element.dataset.i18nPlaceholder,
      variablesForElement(element),
      normalized,
    ));
  });
  root.querySelectorAll('[data-i18n-aria-label]').forEach((element) => {
    element.setAttribute('aria-label', t(
      element.dataset.i18nAriaLabel,
      variablesForElement(element),
      normalized,
    ));
  });
  root.querySelectorAll('[data-i18n-date]').forEach((element) => {
    element.textContent = formatLocalizedDate(Number(element.dataset.i18nDate), normalized);
  });
  root.querySelectorAll('[data-i18n-cents]').forEach((element) => {
    element.textContent = formatLocalizedCurrency(Number(element.dataset.i18nCents), normalized);
  });
}
